/**
 * AchievementsPage - Gamification & leaderboard
 * ZAPHUB 2.0 - Unlock achievements, compete, earn rewards
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Trophy,
  Award,
  Star,
  Zap,
  Target,
  Crown,
  Medal,
  Sparkles,
  TrendingUp,
  Gift,
  Lock,
  Unlock,
  Flame,
  CheckCircle,
} from 'lucide-react';
import type { Achievement, Leaderboard } from '@shared/types';

export const AchievementsPage: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [leaderboard, setLeaderboard] = useState<Leaderboard[]>([]);
  const [userStats, setUserStats] = useState({
    rank: 5,
    score: 2450,
    totalPoints: 5000,
    level: 12,
    nextLevelAt: 6000,
    achievementsUnlocked: 15,
    totalAchievements: 30,
    streak: 7,
  });

  useEffect(() => {
    fetchAchievements();
    fetchLeaderboard();
  }, []);

  const fetchAchievements = async () => {
    // Mock data - in production, call API
    const mockAchievements: Achievement[] = [
      {
        id: '1',
        userId: 'current-user',
        type: 'workflow_created',
        title: 'First Steps',
        description: 'Create your first workflow',
        icon: '🚀',
        badgeUrl: '/badges/first-workflow.png',
        bonusAmount: 50,
        unlockedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      },
      {
        id: '2',
        userId: 'current-user',
        type: 'executions',
        title: 'Centurion',
        description: 'Execute 100 workflows',
        icon: '⚡',
        badgeUrl: '/badges/100-executions.png',
        bonusAmount: 100,
        unlockedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      },
      {
        id: '3',
        userId: 'current-user',
        type: 'marketplace_sale',
        title: 'Entrepreneur',
        description: 'Make your first marketplace sale',
        icon: '💰',
        badgeUrl: '/badges/first-sale.png',
        bonusAmount: 200,
        unlockedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      },
      {
        id: '4',
        userId: 'current-user',
        type: 'rating',
        title: 'Five Stars',
        description: 'Get a 5-star rating on marketplace',
        icon: '⭐',
        badgeUrl: '/badges/five-stars.png',
        bonusAmount: 150,
        unlockedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      },
      {
        id: '5',
        userId: 'current-user',
        type: 'streak',
        title: 'Weekly Warrior',
        description: 'Login 7 days in a row',
        icon: '🔥',
        badgeUrl: '/badges/7-day-streak.png',
        bonusAmount: 75,
        unlockedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      },
    ];

    setAchievements(mockAchievements);
  };

  const fetchLeaderboard = async () => {
    // Mock data - in production, call API
    const mockLeaderboard: Leaderboard[] = [
      {
        userId: 'user1',
        userName: 'Sarah Johnson',
        avatar: undefined,
        workflowsCreated: 45,
        workflowsApproved: 42,
        totalBonuses: 21000,
        marketplaceSales: 156,
        achievements: 28,
        rank: 1,
        score: 8950,
      },
      {
        userId: 'user2',
        userName: 'Michael Chen',
        avatar: undefined,
        workflowsCreated: 38,
        workflowsApproved: 35,
        totalBonuses: 17500,
        marketplaceSales: 98,
        achievements: 25,
        rank: 2,
        score: 7280,
      },
      {
        userId: 'user3',
        userName: 'Emma Williams',
        avatar: undefined,
        workflowsCreated: 32,
        workflowsApproved: 30,
        totalBonuses: 15000,
        marketplaceSales: 76,
        achievements: 22,
        rank: 3,
        score: 6150,
      },
      {
        userId: 'user4',
        userName: 'David Rodriguez',
        avatar: undefined,
        workflowsCreated: 28,
        workflowsApproved: 26,
        totalBonuses: 13000,
        marketplaceSales: 54,
        achievements: 20,
        rank: 4,
        score: 5320,
      },
      {
        userId: 'current-user',
        userName: 'You (John Doe)',
        avatar: undefined,
        workflowsCreated: 25,
        workflowsApproved: 23,
        totalBonuses: 11500,
        marketplaceSales: 45,
        achievements: 15,
        rank: 5,
        score: 4950,
      },
      {
        userId: 'user6',
        userName: 'Lisa Anderson',
        avatar: undefined,
        workflowsCreated: 22,
        workflowsApproved: 20,
        totalBonuses: 10000,
        marketplaceSales: 38,
        achievements: 18,
        rank: 6,
        score: 4520,
      },
      {
        userId: 'user7',
        userName: 'James Wilson',
        avatar: undefined,
        workflowsCreated: 20,
        workflowsApproved: 18,
        totalBonuses: 9000,
        marketplaceSales: 32,
        achievements: 16,
        rank: 7,
        score: 4100,
      },
    ];

    setLeaderboard(mockLeaderboard);
  };

  const lockedAchievements = [
    {
      title: 'Master Creator',
      description: 'Create 50 workflows',
      icon: '👑',
      bonusAmount: 500,
      progress: (userStats.totalPoints / 50) * 100,
      requirement: '25/50 workflows',
    },
    {
      title: 'Sales Champion',
      description: 'Earn $10,000 from marketplace',
      icon: '💎',
      bonusAmount: 1000,
      progress: 75,
      requirement: '$7,500 / $10,000',
    },
    {
      title: 'Perfectionist',
      description: '100% success rate over 100 executions',
      icon: '✨',
      bonusAmount: 300,
      progress: 92,
      requirement: '92/100 perfect runs',
    },
    {
      title: 'Team Player',
      description: 'Help 10 colleagues with workflows',
      icon: '🤝',
      bonusAmount: 250,
      progress: 60,
      requirement: '6/10 colleagues helped',
    },
  ];

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="h-6 w-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <Medal className="h-6 w-6 text-orange-400" />;
    return <Award className="h-6 w-6 text-gray-400" />;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500';
    if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600';
    return 'bg-gray-200';
  };

  const levelProgress = ((userStats.totalPoints - (userStats.nextLevelAt - 1000)) / 1000) * 100;

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl mb-2">Achievements & Rewards</h1>
        <p className="text-gray-600">
          Unlock achievements, compete on the leaderboard, and earn bonuses
        </p>
      </div>

      {/* User Stats Card */}
      <Card className="p-6 mb-8 bg-gradient-to-br from-purple-500 to-pink-500 text-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Level & Progress */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                <Star className="h-8 w-8" />
              </div>
              <div>
                <div className="text-3xl">Level {userStats.level}</div>
                <div className="text-white/80">
                  {userStats.totalPoints} / {userStats.nextLevelAt} XP
                </div>
              </div>
            </div>
            <Progress value={levelProgress} className="h-3 bg-white/20" />
            <div className="text-sm text-white/80 mt-2">
              {userStats.nextLevelAt - userStats.totalPoints} XP to next level
            </div>
          </div>

          {/* Rank */}
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
              {getRankIcon(userStats.rank)}
            </div>
            <div>
              <div className="text-sm text-white/80">Global Rank</div>
              <div className="text-3xl">#{userStats.rank}</div>
            </div>
          </div>

          {/* Achievements */}
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
              <Trophy className="h-8 w-8" />
            </div>
            <div>
              <div className="text-sm text-white/80">Achievements</div>
              <div className="text-3xl">
                {userStats.achievementsUnlocked}/{userStats.totalAchievements}
              </div>
            </div>
          </div>
        </div>

        {/* Streak */}
        <div className="mt-6 pt-6 border-t border-white/20 flex items-center gap-2">
          <Flame className="h-5 w-5" />
          <span className="text-lg">{userStats.streak} day streak!</span>
          <span className="text-white/80 ml-auto">Keep it up to unlock bonus rewards</span>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="achievements" className="w-full">
        <TabsList>
          <TabsTrigger value="achievements">
            <Trophy className="h-4 w-4 mr-2" />
            Achievements
          </TabsTrigger>
          <TabsTrigger value="leaderboard">
            <Crown className="h-4 w-4 mr-2" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="rewards">
            <Gift className="h-4 w-4 mr-2" />
            Rewards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="achievements" className="mt-6">
          {/* Unlocked Achievements */}
          <div className="mb-8">
            <h3 className="text-xl mb-4">Unlocked ({achievements.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {achievements.map((achievement) => (
                <Card key={achievement.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-4xl">{achievement.icon}</div>
                    <Badge variant="default" className="bg-green-500">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Unlocked
                    </Badge>
                  </div>
                  <h4 className="text-lg mb-2">{achievement.title}</h4>
                  <p className="text-sm text-gray-600 mb-4">{achievement.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-green-600">
                      <Gift className="h-4 w-4" />
                      <span>+${achievement.bonusAmount}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(achievement.unlockedAt).toLocaleDateString()}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Locked Achievements */}
          <div>
            <h3 className="text-xl mb-4">Locked ({lockedAchievements.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lockedAchievements.map((achievement, index) => (
                <Card key={index} className="p-6 opacity-75">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-4xl grayscale">{achievement.icon}</div>
                    <Badge variant="secondary">
                      <Lock className="h-3 w-3 mr-1" />
                      Locked
                    </Badge>
                  </div>
                  <h4 className="text-lg mb-2">{achievement.title}</h4>
                  <p className="text-sm text-gray-600 mb-4">{achievement.description}</p>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Progress</span>
                      <span className="text-purple-600">{achievement.progress.toFixed(0)}%</span>
                    </div>
                    <Progress value={achievement.progress} className="h-2" />
                    <div className="text-xs text-gray-500 mt-1">{achievement.requirement}</div>
                  </div>

                  <div className="flex items-center gap-1 text-sm text-purple-600">
                    <Gift className="h-4 w-4" />
                    <span>Reward: +${achievement.bonusAmount}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <Card className="p-6">
            <h3 className="text-xl mb-6">Global Leaderboard</h3>
            <div className="space-y-3">
              {leaderboard.map((user) => (
                <div
                  key={user.userId}
                  className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                    user.userId === 'current-user'
                      ? 'bg-purple-50 border-2 border-purple-500'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {/* Rank */}
                  <div className="flex items-center justify-center w-12">
                    {user.rank <= 3 ? (
                      <div className={`h-10 w-10 rounded-full ${getRankColor(user.rank)} flex items-center justify-center text-white`}>
                        {getRankIcon(user.rank)}
                      </div>
                    ) : (
                      <div className="text-2xl text-gray-400">#{user.rank}</div>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl">
                    {user.userName.split(' ').map((n) => n[0]).join('')}
                  </div>

                  {/* User Info */}
                  <div className="flex-1">
                    <div className="mb-1">
                      <span className="text-lg">{user.userName}</span>
                      {user.userId === 'current-user' && (
                        <Badge variant="default" className="ml-2 bg-purple-500">You</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>{user.workflowsCreated} workflows</span>
                      <span>{user.marketplaceSales} sales</span>
                      <span>{user.achievements} achievements</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right">
                    <div className="text-2xl mb-1">{user.score.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">points</div>
                  </div>

                  <div className="text-right">
                    <div className="text-lg text-green-600 mb-1">
                      ${user.totalBonuses.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">earned</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="rewards" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Rewards */}
            <Card className="p-6">
              <h3 className="text-xl mb-4 flex items-center gap-2">
                <Gift className="h-6 w-6 text-purple-500" />
                Available Rewards
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg">Achievement Bonus</span>
                    <Badge className="bg-green-500">Ready to Claim</Badge>
                  </div>
                  <div className="text-3xl text-green-600 mb-2">$150</div>
                  <Button className="w-full bg-green-500 hover:bg-green-600">
                    <Gift className="h-4 w-4 mr-2" />
                    Claim Reward
                  </Button>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg">Weekly Streak Bonus</span>
                    <Badge variant="secondary">In Progress</Badge>
                  </div>
                  <div className="text-2xl text-blue-600 mb-2">$75</div>
                  <Progress value={(userStats.streak / 7) * 100} className="h-2 mb-2" />
                  <div className="text-sm text-gray-600">
                    {userStats.streak}/7 days completed
                  </div>
                </div>
              </div>
            </Card>

            {/* Reward History */}
            <Card className="p-6">
              <h3 className="text-xl mb-4 flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-yellow-500" />
                Reward History
              </h3>
              <div className="space-y-3">
                {[
                  { title: 'Five Stars Achievement', amount: 150, date: '2 days ago' },
                  { title: 'Weekly Warrior', amount: 75, date: '1 week ago' },
                  { title: 'Entrepreneur Achievement', amount: 200, date: '2 weeks ago' },
                  { title: 'Centurion Achievement', amount: 100, date: '3 weeks ago' },
                ].map((reward, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="mb-1">{reward.title}</div>
                      <div className="text-sm text-gray-500">{reward.date}</div>
                    </div>
                    <div className="text-lg text-green-600">+${reward.amount}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Total Earnings */}
          <Card className="p-6 mt-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white/80 mb-1">Total Lifetime Earnings</div>
                <div className="text-4xl">${userStats.totalPoints.toLocaleString()}</div>
              </div>
              <TrendingUp className="h-16 w-16 text-white/50" />
            </div>
            <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between text-sm">
              <span>This month: $850</span>
              <span className="text-white/80">+42% vs last month</span>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
